import { useEffect, useRef } from 'react';
import { analytics } from './events';

export const ANALYTICS_ROUTE_CHANGE = 'analytics:routechange';

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;
const HEARTBEAT_MS = 30_000;
const EMAIL_RE = /\S+@\S+\.\S+/;

function closestAnchor(node: EventTarget | null): HTMLAnchorElement | null {
  if (!(node instanceof Element)) return null;
  return node.closest('a');
}

function classifyHref(href: string): 'internal' | 'external' | 'download' | 'mailto' | 'tel' | 'hash' {
  if (href.startsWith('mailto:')) return 'mailto';
  if (href.startsWith('tel:')) return 'tel';
  if (href.startsWith('#')) return 'hash';
  if (/\.(pdf|zip|csv|json|png|jpg|jpeg|webp|svg|mp3|wav|ogg)(\?|$)/i.test(href)) return 'download';
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      return url.hash && url.pathname === window.location.pathname ? 'hash' : 'internal';
    }
    return 'external';
  } catch {
    return 'internal';
  }
}

function scrollPercent(): number {
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (docHeight <= 0) return 100;
  return Math.round((window.scrollY / docHeight) * 100);
}

function copySource(target: EventTarget | null): string {
  if (!(target instanceof Element)) return 'selection';
  if (target.closest('pre, code, [data-language]')) return 'code';
  if (target.closest('blockquote')) return 'blockquote';
  if (window.location.pathname.startsWith('/quotes')) return 'quote';
  if (window.location.pathname.includes('/articles/') || window.location.pathname.includes('/blog/posts/')) {
    return 'article';
  }
  return 'selection';
}

export function GlobalCapture() {
  const startRef = useRef(Date.now());
  const visibleAtRef = useRef(Date.now());
  const clicksRef = useRef(0);
  const keysRef = useRef(0);
  const maxScrollRef = useRef(0);
  const scrollFiredRef = useRef<Set<number>>(new Set());
  const pathRef = useRef(typeof window === 'undefined' ? '' : window.location.pathname);

  useEffect(() => {
    const flushEngagement = (reason: string) => {
      const timeMs = Date.now() - startRef.current;
      if (timeMs < 1500 && clicksRef.current === 0 && keysRef.current === 0) return;
      analytics.pageEngagement(
        timeMs,
        maxScrollRef.current,
        clicksRef.current,
        keysRef.current,
        reason,
      );
    };

    const onClick = (event: MouseEvent) => {
      clicksRef.current += 1;
      const anchor = closestAnchor(event.target);
      if (!anchor?.href) return;

      const href = anchor.href;
      const kind = classifyHref(href);
      const text = (anchor.textContent || '').trim();
      analytics.linkClicked(href, kind, text);

      if (kind === 'hash') {
        const hash = new URL(href, window.location.origin).hash;
        if (hash.includes('user-content-fn')) {
          analytics.footnoteClicked(hash.slice(1));
        } else {
          analytics.headingAnchorClicked(hash);
        }
      }
    };

    const onCopy = (event: ClipboardEvent) => {
      const text = window.getSelection()?.toString() ?? '';
      if (!text) return;
      const preview = EMAIL_RE.test(text) ? undefined : text.slice(0, 80);
      analytics.contentCopied(copySource(event.target), text.length, preview);
    };

    const onPaste = (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData('text/plain') ?? '';
      if (!text) return;
      analytics.contentPasted(copySource(event.target), text.length);
    };

    const onSubmit = (event: Event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const id = form.id || form.getAttribute('aria-label') || form.getAttribute('name') || 'anonymous';
      analytics.formSubmitted(id, form.elements.length);
    };

    const onDrop = (event: DragEvent) => {
      const files = event.dataTransfer?.files;
      if (!files?.length) return;
      const types = [...new Set(Array.from(files).map((file) => file.type || 'unknown'))];
      const totalBytes = Array.from(files).reduce((sum, file) => sum + file.size, 0);
      analytics.filesDropped(files.length, types, Math.round((totalBytes / 1024 / 1024) * 100) / 100);
    };

    const onConnection = () => {
      analytics.connectionChanged(navigator.onLine);
    };

    const onScroll = () => {
      const pct = scrollPercent();
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !scrollFiredRef.current.has(threshold)) {
          scrollFiredRef.current.add(threshold);
          analytics.scrollDepth(threshold);
        }
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      keysRef.current += 1;
      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        analytics.printInitiated();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        analytics.tabHidden(Date.now() - visibleAtRef.current);
        flushEngagement('hidden');
      } else {
        visibleAtRef.current = Date.now();
        analytics.tabVisible();
      }
    };

    const onPageHide = () => {
      flushEngagement('pagehide');
    };

    const onPrint = () => {
      analytics.printInitiated();
    };

    const onMediaPlay = (event: Event) => {
      const el = event.target;
      if (!(el instanceof HTMLMediaElement)) return;
      analytics.mediaPlayed(el.tagName.toLowerCase(), el.currentSrc || el.src);
    };

    const onMediaPause = (event: Event) => {
      const el = event.target;
      if (!(el instanceof HTMLMediaElement)) return;
      analytics.mediaPaused(el.tagName.toLowerCase(), el.currentSrc || el.src);
    };

    const heartbeat = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      analytics.pageEngagement(
        Date.now() - startRef.current,
        maxScrollRef.current,
        clicksRef.current,
        keysRef.current,
        'heartbeat',
      );
    }, HEARTBEAT_MS);

    document.addEventListener('click', onClick, true);
    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('drop', onDrop, true);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeprint', onPrint);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('online', onConnection);
    window.addEventListener('offline', onConnection);
    document.addEventListener('play', onMediaPlay, true);
    document.addEventListener('pause', onMediaPause, true);

    onScroll();

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('submit', onSubmit, true);
      document.removeEventListener('drop', onDrop, true);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeprint', onPrint);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('online', onConnection);
      window.removeEventListener('offline', onConnection);
      document.removeEventListener('play', onMediaPlay, true);
      document.removeEventListener('pause', onMediaPause, true);
    };
  }, []);

  useEffect(() => {
    const resetEngagement = (reason: string) => {
      analytics.pageEngagement(
        Date.now() - startRef.current,
        maxScrollRef.current,
        clicksRef.current,
        keysRef.current,
        reason,
      );
      startRef.current = Date.now();
      visibleAtRef.current = Date.now();
      clicksRef.current = 0;
      keysRef.current = 0;
      maxScrollRef.current = 0;
      scrollFiredRef.current = new Set();
      pathRef.current = window.location.pathname;
    };

    const onRouteChange = () => resetEngagement('spa');

    window.addEventListener(ANALYTICS_ROUTE_CHANGE, onRouteChange);
    return () => {
      window.removeEventListener(ANALYTICS_ROUTE_CHANGE, onRouteChange);
    };
  }, []);

  return null;
}
